/**
 * @module: KR下任务
 * @author: yuanxm
 * @date: 2020-05-16 11:07:34
 */
import { observable, action } from 'mobx';
import { ajax } from '@clouderwork/ywk';
import { IOssFile } from '@src/utils/upload';
import company from '@src/store/company';
import { Toast } from 'antd-mobile';
import { ITaskDetail } from '@src/entity/okr/task';

class OKR {
  // 附件
  @observable attachmentList: IOssFile[] = [];
  // 参与者
  @observable followerList: any[] = [];
  // 获取任务详情的状态
  @observable fetching = 'init';
  // 任务详情
  @observable taskDetail?: ITaskDetail;
  // 修改任务接口状态
  @observable fetching_edit = 'init';
  // 评论列表
  @observable commentList: any[] = [];
  // 任务验收记录列表
  @observable processRecordList: any[] = [];
  // 正在获取评论
  @observable fetchingComment: string = 'pending';
  // 评论总数量
  @observable commentTotal: number = 0;
  // 评论页数
  @observable commentPageNum: number = 1;
  // 正在获取验收记录
  @observable fetchingProcessRecord: string = 'init';

  @observable attachmentDeleteFetching: string = 'init';

  @action.bound
  updateTaskDetail(obj: { [key: string]: any }) {
    if (this.taskDetail) {
      this.taskDetail = { ...this.taskDetail, ...obj };
    }
  }

  @action.bound
  async getTaskDetail(id: number | string) {
    this.fetching = 'pending';
    const res = await ajax.getJson(`/okr/api/tasks/${id}`);

    if (res.error_code === 0) {
      const { task } = res.result;
      this.fetching = 'success';
      this.taskDetail = task;

      // 设置关联目标和关联项目弹窗中需要的当前数据
      company.taskOKR.setCurData(
        task.target.id ? (task.target as any) : undefined,
        task.achievement.id ? (task.achievement as any) : undefined
      );
      company.taskProject.setCurData(
        task.project.uuid ? (task.project as any) : undefined,
        task.kanban_id
          ? ({
              id: task.kanban_id,
              name: task.kanban_name
            } as any)
          : undefined
      );
      return res.result.task;
    } else {
      this.fetching = 'fail';
      Toast.fail(res.msg, 1);
    }
  }

  async editTask(id: string | number, params: any) {
    this.fetching_edit = 'pending';
    const res = await ajax.putPureJson(`/okr/api/tasks/${id}`, params);

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }
    Toast.fail(res.msg, 1);
    return false;
  }

  async moveTask(task_id: string | number, kanban_id: number) {
    this.fetching_edit = 'pending';
    const res = await ajax.putPureJson('/okr/api/tasks/move', {
      task_id: [task_id],
      kanban_id
    });

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }
    Toast.fail(res.msg, 1);
    return false;
  }

  async editIntegral(id: string | number, award_integral: number) {
    this.fetching_edit = 'pending';
    const res = await ajax.putPureJson(`/okr/api/okr/task/internal/${id}`, {
      award_integral
    });

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }
    Toast.fail(res.msg, 1);
    return false;
  }

  /**
   * 批量修改标签
   *
   * @param {string} id 任务id
   * @param {number[]} tag_id tag id列表
   * @memberof OKR
   */
  async editTags(id: string | number, tag_id: number[]) {
    this.fetching_edit = 'pending';
    const res = await ajax.post(`/okr/api/tasks/${id}/tag/batch`, {
      tag_id
    });

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }
    Toast.fail(res.msg, 1);
    return false;
  }

  /**
   * 给任务删除负责人或参与者
   *
   * @param {(string | number)} id
   * @param {({ user: string; follow_type: 1 | 2 })} params user:人员uuid follow_type:1负责人 2:指派人
   * @returns
   * @memberof OKR
   */
  async delFollower(id: string | number, params: { user: string; follow_type: 1 | 2 }) {
    this.fetching_edit = 'pending';
    const res = await ajax.deletePureJson(`/okr/api/tasks/follow/${id}`, params);

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }
    Toast.fail(res.msg, 1);
    return false;
  }

  /**
   * 给任务添加负责人或参与者或验收人
   *
   * @param {string | number} id
   * @param {string} user 用户uuid
   * @param {(1 | 2 | 3)} follow_type 1_ 负责人,, 2_ 指派人 , 3_验收人
   * @returns
   * @memberof OKR
   */
  async addPerson(id: string | number, user: string, follow_type: 1 | 2 | 3) {
    this.fetching_edit = 'pending';
    const res = await ajax.post(`/okr/api/tasks/follow/${id}`, {
      user,
      follow_type
    });

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }

    Toast.fail(res.msg, 1);
    return false;
  }

  /**
   * 批量修改任务参与者
   *
   * @param {(string | number)} id
   * @param {string[]} follow_id 用户uuid数组
   * @returns
   * @memberof OKR
   */
  async editFollowers(id: string | number, follow_id: string[]) {
    this.fetching_edit = 'pending';
    const res = await ajax.post('/okr/api/tasks/follow/batch', {
      task_id: id,
      follow_id
    });

    this.fetching_edit = 'done';
    if (res.error_code === 0) {
      return true;
    }
    Toast.fail(res.msg, 1);
    return false;
  }

  async getCommentList(id: string | number, pagenum: number = 1) {
    this.fetchingComment = 'pending';
    const res = await ajax.getJson(`/okr/api/tasks/comment/${id}`, {
      pagenum,
      pagesize: 10
    });

    this.fetchingComment = 'done';

    if (res.error_code === 0) {
      if (res.pagenum === 1) {
        this.commentList = res.result;
      } else {
        this.commentList = [...this.commentList, ...res.result];
      }
      this.commentPageNum = res.pagenum;
      this.commentTotal = res.total;
      return true;
    }

    return false;
  }

  async getProcessRecordList(id: string | number) {
    this.fetchingProcessRecord = 'pending';
    const res = await ajax.getJson(`/okr/api/okr/task/process/${id}`);

    this.fetchingProcessRecord = 'done';

    if (res.error_code === 0) {
      this.processRecordList = res.result;
      return true;
    }

    return false;
  }
  /**
   * 发送任务消息
   */
  @action.bound
  async sendTaskMessage(data, project_id: string, content: string) {
    if (!data) return;
    const { id, keyword, category, subject, category_text, end_at, comment_num, attach_num, tags } = data;
    const params = {
      project_uuid: project_id,
      content: {
        id,
        keyword,
        category,
        subject,
        category_text,
        end_at,
        comment_num,
        attach_num,
        tags,
        project_name: data.project.name,
        slug: project_id
      }
    };
    try {
      const res = await ajax.post(`/company/api/tasks/${data.id}/send-to-chat`, params);
      if (content) {
        await ajax.post(`/wokexin/api/v1/message.send?usergroup_id=${company.user.user.company_uuid}`, {
          subtype: 'text',
          type: 'message',
          data: {
            text: content
          },
          from: '4',
          ref_id: project_id
        });
      }
      if (+res.error_code !== 0) {
        Toast.fail(res.msg || res.message, 1);
      }
      return res;
    } catch (error) {
      return { msg: '请求错误' };
    }
  }
}

export default OKR;
